import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Table,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Button,
} from "react-bootstrap";
import moment from "moment";
import { Clock } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { BOOKING_HISTORY_DATA } from "../../../../../services/ApiCalls";
import { checkResponse } from "../../../../../utilities/utilities";

const TAB_TO_FILTER = {
  upcoming: "upcoming",
  stayInHouse: "checkedIn",
  checkedOut: "today",
};

const Reservations = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("checkedOut");
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 10;

  const tableRef = useRef(null);

  /* ---------------- API CALL ---------------- */
  useEffect(() => {
    const payload = {
      page,
      limit,
      filter: TAB_TO_FILTER[activeTab],
    };

    setLoading(true);

    BOOKING_HISTORY_DATA(payload).then((res) => {
      checkResponse({
        res,
        setData,
        setLoader: setLoading,
      });
    });
  }, [activeTab, page]);

  /* ---------------- Filters (client-side) ---------------- */
  const filteredData = useMemo(() => {
    return data.filter((r) => {
      const name = `${r.customerId?.first_name ?? ""} ${
        r.customerId?.last_name ?? ""
      }`
        .toLowerCase()
        .trim();

      return (
        name.includes(search.toLowerCase()) ||
        r.bookingId?.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [data, search]);

  /* ---------------- Auto scroll ---------------- */
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [activeTab, page]);

  /* ---------------- Tabs ---------------- */
  const tabs = [
    { key: "upcoming", label: t("dashboard.reservations.tabs.upcoming") },
    { key: "stayInHouse", label: t("dashboard.reservations.tabs.checkedIn") },
    { key: "checkedOut", label: t("dashboard.reservations.tabs.today") },
  ];

  return (
    <Card className="shadow-sm rounded-4 border-0">
      <Card.Body className="px-4 py-4">
        {/* ---------------- Header ---------------- */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">
            {t("dashboard.reservations.bookingActivity","Booking Activity")}
          </h5>
          {loading && <Spinner size="sm" />}
        </div>

        {/* ---------------- Tabs ---------------- */}
        <div className="d-flex gap-4 border-bottom pb-2 mb-3">
          {tabs.reverse().map((tab) => (
            <div
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setPage(1);
              }}
              role="button"
              className={`pb-2 ${
                activeTab === tab.key
                  ? "border-bottom border-2 border-danger"
                  : ""
              }`}
              style={{
                fontSize: "14px",
                fontWeight: activeTab === tab.key ? "600" : "400",
                color: activeTab === tab.key ? "#E8307A" : "#6c757d",
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {/* ---------------- Search ---------------- */}
        <div className="position-relative mb-3">
          <span
            style={{
              position: "absolute",
              left: "12px",
              top: "8px",
              opacity: 0.6,
            }}
          >
            🔍
          </span>
          <Form.Control
            type="text"
            placeholder={t("dashboard.reservations.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-pill ps-5 py-2"
            style={{ fontSize: "14px" }}
          />
        </div>

        {/* ---------------- Table ---------------- */}
        <div
          ref={tableRef}
          className="table-responsive"
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <Table hover className="align-middle mb-0">
            {/* ===== THEAD (UNCHANGED) ===== */}
            <thead className="sticky-top bg-white">
              <tr>
                {[
                  t("dashboard.reservations.table.name"),
                  t("dashboard.reservations.table.bookingId"),
                  t("dashboard.reservations.table.type"),
                  t("dashboard.reservations.table.price"),
                  t("dashboard.reservations.table.checkIn"),
                ].map((item) => (
                  <th
                    key={item}
                    className="border-0 text-muted fw-normal"
                    style={{ fontSize: "12px" }}
                  >
                    {item}
                  </th>
                ))}
              </tr>
            </thead>

            {/* ---------------- Body ---------------- */}
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <Spinner size="sm" />
                  </td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((res, i) => {
                  const guestName = `${res.customerId?.first_name ?? ""} ${
                    res.customerId?.last_name ?? ""
                  }`;

                  return (
                    <tr key={res._id || i}>
                      <td className="fw-semibold" style={{ color: "#E8307A" }}>
                        {guestName || "-"}
                      </td>
                      <td className="text-muted">{res.bookingId || "-"}</td>
                      <td className="text-muted text-capitalize">
                        {res.priceDetail?.pricingType || "-"}
                      </td>
                      <td className="fw-bold">
                        {res.currencySymbol}
                        {res.finalPrice}
                      </td>
                      <td className="text-center">
                        {res.checkInDate ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${i}`}>
                                {moment(res.checkInDate).format(
                                  "dddd, MMMM Do YYYY, h:mm A"
                                )}
                              </Tooltip>
                            }
                          >
                            <span>
                              <Clock size={16} className="text-secondary" />
                            </span>
                          </OverlayTrigger>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-3">
                    {t("dashboard.reservations.noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* ---------------- Pagination ---------------- */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={page === 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={loading || filteredData.length < limit}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default Reservations;
